// src/pages/QuestionnaireForm.tsx
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { saveQuestionnaire } from '../api/questionnaire';
import { Helmet } from 'react-helmet';
import classNames from 'classnames';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Send, CheckCircle2, UserCircle } from 'lucide-react';

type LangKey = 'ja' | 'zh';
type QuestionType = 'choice' | 'short_text' | 'long_text' | 'number' | 'multi_choice';

type VisibleCondition = {
  id: string;
  equals?: string[];
  notEquals?: string[];
  includes?: string[];
  notIncludes?: string[];
};

interface BaseQuestion {
  id: string;
  text: { ja: string; zh: string };
  type?: QuestionType;                 // デフォルト: 'choice'
  required?: boolean;                  // デフォルト: true
  placeholder?: { ja: string; zh: string };
  maxLength?: number;
  inputMode?: 'text' | 'tel' | 'email' | 'numeric';
  visible_if?: VisibleCondition[];     // 可視条件
}

interface ChoiceQuestion extends BaseQuestion {
  type?: 'choice';
  options: { ja: string[]; zh: string[] };
}

interface MultiChoiceQuestion extends BaseQuestion {
  type: 'multi_choice';
  options: { ja: string[]; zh: string[] };
}

interface TextQuestion extends BaseQuestion {
  type: 'short_text' | 'long_text' | 'number';
  options?: never;
}

type Question = ChoiceQuestion | TextQuestion | MultiChoiceQuestion;
type QuestionsRaw = Question[] | { first?: Question[]; followup?: Question[] };

interface Props {
  questionFile: string; // 例: 'aga_male_questions.json'
  service: string;
}

const QuestionnaireForm: React.FC<Props> = ({ questionFile, service }) => {
  const { i18n } = useTranslation();
  const lang = (i18n.language?.startsWith('zh') ? 'zh' : 'ja') as LangKey;
  const navigate = useNavigate();
  const location = useLocation() as any;

  const { reservationId, visitType: vtFromState, visit_type: vtSnake } = location.state || {};
  const preferredVisitType: 'first' | 'followup' | undefined =
    vtFromState === 'followup' || vtSnake === 'followup'
      ? 'followup'
      : vtFromState === 'first' || vtSnake === 'first'
        ? 'first'
        : undefined;

  const [questions, setQuestions] = useState<Question[]>([]);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string | string[]>>({});
  const [draftInput, setDraftInput] = useState<string>('');
  const [draftMulti, setDraftMulti] = useState<Set<string>>(new Set());
  const [draftChoice, setDraftChoice] = useState<string>('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!reservationId) navigate('/mypage');
  }, [reservationId, navigate]);

  const pickGroup = (raw: QuestionsRaw): Question[] => {
    if (Array.isArray(raw)) return raw;
    const first = raw.first || [];
    const follow = raw.followup || [];
    if (preferredVisitType) {
      if (preferredVisitType === 'followup' && follow.length) return follow;
      if (preferredVisitType === 'first' && first.length) return first;
      return first.length ? first : follow;
    }
    if (first.length && follow.length) return first;
    return first.length ? first : follow;
  };

  const dataUrl = (filename: string) => {
    const base = process.env.PUBLIC_URL || '/';
    const prefix = base.endsWith('/') ? base : `${base}/`;
    return `${prefix}data/${filename}`;
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(dataUrl(questionFile), { cache: 'no-store' });
        const text = await res.text();
        const raw = JSON.parse(text) as QuestionsRaw;
        if (!alive) return;
        const selected = pickGroup(raw);
        setQuestions(selected);
        setLoading(false);
      } catch {
        if (!alive) return;
        setError(lang === 'ja' ? '問診票の読み込みに失敗しました' : '问诊票加载失败');
        setLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionFile, lang, preferredVisitType]);

  const toArray = (v: string | string[] | undefined | null): string[] => {
    if (Array.isArray(v)) return v;
    if (typeof v === 'string' && v.length > 0) {
      return v.split('、').map((s) => s.trim()).filter(Boolean);
    }
    return [];
  };

  const normalizeToken = (s: string) =>
    s.replace(/（/g, '(').replace(/）/g, ')').trim();

  const isVisibleWith = useCallback(
    (qq: Question, baseAnswers: Record<string, string | string[]>): boolean => {
      if (!qq.visible_if || qq.visible_if.length === 0) return true;
      return qq.visible_if.every((cond) => {
        const arr = toArray(baseAnswers[cond.id]);
        const normSet = new Set(arr.map((x) => normalizeToken(x)));
        if (cond.equals && cond.equals.length > 0) {
          const ok = cond.equals.some((v) => normSet.has(normalizeToken(v)));
          if (!ok) return false;
        }
        if (cond.notEquals && cond.notEquals.length > 0) {
          const ng = cond.notEquals.some((v) => normSet.has(normalizeToken(v)));
          if (ng) return false;
        }
        if (cond.includes && cond.includes.length > 0) {
          const ok = cond.includes.some((v) => normSet.has(normalizeToken(v)));
          if (!ok) return false;
        }
        if (cond.notIncludes && cond.notIncludes.length > 0) {
          const ng = cond.notIncludes.some((v) => normSet.has(normalizeToken(v)));
          if (ng) return false;
        }
        return true;
      });
    },
    []
  );

  const computeVisible = useCallback(
    (qs: Question[], baseAnswers: Record<string, string | string[]>) =>
      qs.filter((qq) => isVisibleWith(qq, baseAnswers)),
    [isVisibleWith]
  );

  const visibleQuestions = useMemo(
    () => computeVisible(questions, answers),
    [questions, answers, computeVisible]
  );

  const q = visibleQuestions[current];

  useEffect(() => {
    if (current > visibleQuestions.length - 1) {
      setCurrent(Math.max(0, visibleQuestions.length - 1));
    }
    const visibleIds = new Set(visibleQuestions.map((x) => x.id));
    setAnswers((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const qu of questions) {
        if (qu.visible_if && !visibleIds.has(qu.id) && next[qu.id] !== undefined) {
          delete next[qu.id];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [visibleQuestions, questions, current]);

  useEffect(() => {
    if (!q) return;
    const prev = answers[q.id];
    if (q.type === 'multi_choice') {
      let items: string[] = [];
      if (Array.isArray(prev)) items = prev as string[];
      else if (typeof prev === 'string') items = prev.split('、').map((s) => s.trim()).filter(Boolean);
      setDraftMulti(new Set(items));
      setDraftInput('');
      setDraftChoice('');
    } else if (!q.type || q.type === 'choice') {
      const v = typeof prev === 'string' ? prev : '';
      setDraftChoice(v || '');
      setDraftInput('');
      setDraftMulti(new Set());
    } else {
      setDraftInput(prev != null ? String(prev) : '');
      setDraftMulti(new Set());
      setDraftChoice('');
    }
    setError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, q?.id]);

  const isRequired = (qq?: Question) => qq?.required ?? true;

  const validateDraft = useCallback(
    (qq?: Question): string | null => {
      if (!qq) return null;
      if (!qq.type || qq.type === 'choice') {
        if (isRequired(qq) && !draftChoice) return lang === 'ja' ? '選択してください' : '请选择一项';
        return null;
      }
      if (qq.type === 'multi_choice') {
        if (isRequired(qq) && draftMulti.size === 0) return lang === 'ja' ? '少なくとも1つ選択してください' : '请至少选择一项';
        return null;
      }
      const v = draftInput;
      if (isRequired(qq) && !v.trim()) return lang === 'ja' ? '入力してください' : '请输入内容';
      if (qq.maxLength && v.length > qq.maxLength) return (lang === 'ja' ? '最大文字数: ' : '最大长度：') + qq.maxLength;
      if (qq.type === 'number' && v.trim()) {
        if (!/^-?\d+(\.\d+)?$/.test(v.trim())) return lang === 'ja' ? '数字で入力してください' : '请输入数字';
      }
      return null;
    },
    [draftInput, draftMulti, draftChoice, lang]
  );

  const collectAnswerForCurrent = (): Record<string, string | string[]> => {
    if (!q) return answers;
    const next = { ...answers };
    if (!q.type || q.type === 'choice') next[q.id] = draftChoice || '';
    else if (q.type === 'multi_choice') next[q.id] = Array.from(draftMulti);
    else next[q.id] = draftInput.trim();
    return next;
  };

  const predictedAnswers = collectAnswerForCurrent();
  const predictedVisible = useMemo(
    () => computeVisible(questions, predictedAnswers),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [questions, draftChoice, draftInput, draftMulti, answers]
  );
  const isLastPredicted = current === predictedVisible.length - 1;

  const handlePrimary = async () => {
    if (!q) return;
    const errMsg = validateDraft(q);
    if (errMsg) { setError(errMsg); return; }
    const nextAnswers = collectAnswerForCurrent();
    setAnswers(nextAnswers);
    setError('');
    if (isLastPredicted) {
      try {
        setSubmitting(true);
        await saveQuestionnaire({ reservationId, service, answers: nextAnswers });
        setSuccess(true);
        setTimeout(() => { navigate('/mypage', { replace: true, state: { refresh: true } }); }, 1000);
      } catch (err: any) {
        const detail = err?.detail;
        setError(detail || (lang === 'ja' ? '問診票の保存に失敗しました。' : '问诊票保存失败'));
      } finally { setSubmitting(false); }
    } else { setCurrent((prev) => prev + 1); }
  };

  const handleBack = useCallback(() => {
    setError('');
    setCurrent((prev) => Math.max(0, prev - 1));
  }, []);

  const onInputChange = (v: string) => { setDraftInput(v); if (error) setError(''); };

  const onKeyDown: React.KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement> = (e) => {
    if (!q) return;
    if ((q.type === 'short_text' || q.type === 'number') && e.key === 'Enter') {
      e.preventDefault();
      handlePrimary();
    }
  };

  const progress = useMemo(() => {
    if (!visibleQuestions.length) return 0;
    const base = (current / visibleQuestions.length) * 100;
    return Math.min(100, Math.round(base));
  }, [current, visibleQuestions.length]);

  if (loading) {
    return (
      <p className="text-center mt-24 text-gray-500">
        {lang === 'ja' ? '読み込み中...' : '加载中...'}
      </p>
    );
  }

  if (success) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-30 flex justify-center items-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm text-center">
          <h2 className="text-lg font-bold text-green-700 mb-4">
            {lang === 'ja' ? '問診票を送信しました' : '问诊票已提交'}
          </h2>
          <p className="text-sm text-gray-700 mb-2">
            {lang === 'ja' ? 'オンライン診療の準備が整いました。' : '线上诊疗准备完毕。'}
          </p>
          <p className="text-xs text-gray-500">
            {lang === 'ja' ? 'まもなくマイページへ移動します…' : '即将跳转至个人页面…'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/50 pb-12">
      <Helmet>
        <title>問診票 - Melodis Clinic</title>
      </Helmet>

      {/* Modern Header */}
      <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-slate-200/60 transition-all">
        <div className="max-w-xl mx-auto px-4 h-16 relative flex items-center justify-between">
          <button
            onClick={() => navigate('/mypage')}
            className="flex items-center gap-1.5 text-slate-500 hover:text-coral transition-colors z-10"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="text-sm font-medium">{lang === 'ja' ? 'マイページ' : '返回主页'}</span>
          </button>
          
          <div className="absolute inset-x-0 inset-y-0 flex flex-col items-center justify-center pointer-events-none px-4">
            <h1 className="text-base font-bold text-slate-900 tracking-tight">
              {lang === 'ja' ? '問診票' : '问诊票'}
            </h1>
            <span className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
              {service.replace(/-/g, ' ')}
            </span>
          </div>

          <div className="w-[84px] pointer-events-none" aria-hidden="true" />
        </div>
        
        {visibleQuestions.length > 0 && (
          <div className="absolute bottom-0 left-0 w-full h-[3px] bg-slate-100">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              className="h-full bg-gradient-primary shadow-[0_0_8px_rgba(255,107,107,0.4)]"
            />
          </div>
        )}
      </header>

      <main className="max-w-xl mx-auto px-4 pt-8">
        <AnimatePresence mode="wait">
          {q ? (
            <motion.div
              key={q.id}
              initial={{ x: 20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -20, opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 p-6 sm:p-8 border border-white"
            >
              <div className="flex flex-col items-center mb-8">
                <span className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-coral/10 text-coral text-xl font-black italic mb-3">
                  {current + 1}
                </span>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em]">
                  Step {current + 1} of {visibleQuestions.length}
                </span>
              </div>

              <h2 className="text-2xl sm:text-3xl font-bold text-slate-800 leading-tight mb-10 text-center">
                {q.text[lang]}
                {!isRequired(q) && (
                  <div className="mt-2 flex justify-center">
                    <span className="px-2 py-0.5 text-[10px] font-bold bg-slate-100 text-slate-400 rounded-md uppercase">
                      {lang === 'ja' ? '任意' : '可选'}
                    </span>
                  </div>
                )}
              </h2>

              <div className="space-y-4 mb-10 min-h-[220px]">
                {(!q.type || q.type === 'choice') && 'options' in q && (
                  <div className="grid grid-cols-1 gap-3">
                    {q.options[lang].map((opt) => {
                      const selected = draftChoice === opt;
                      return (
                        <motion.button
                          key={`${q.id}-${opt}`}
                          whileHover={{ scale: 1.01 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => {
                            setDraftChoice(selected ? '' : opt);
                            if (error) setError('');
                          }}
                          className={classNames(
                            'relative w-full text-left px-5 py-4 rounded-2xl border-2 transition-all duration-200 font-medium',
                            selected
                              ? 'bg-gradient-primary border-transparent text-white shadow-md shadow-coral/20'
                              : 'bg-white border-slate-100 hover:border-coral/30 text-slate-600 hover:bg-slate-50'
                          )}
                        >
                          <div className="flex items-center justify-between">
                            <span>{opt}</span>
                            {selected && <CheckCircle2 className="w-5 h-5 text-white/90" />}
                          </div>
                        </motion.button>
                      );
                    })}
                  </div>
                )}

                {q.type === 'multi_choice' && 'options' in q && (
                  <div className="grid grid-cols-1 gap-3">
                    {q.options[lang].map((opt) => {
                      const checked = draftMulti.has(opt);
                      return (
                        <motion.button
                          key={`${q.id}-${opt}`}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => {
                            const next = new Set(draftMulti);
                            checked ? next.delete(opt) : next.add(opt);
                            setDraftMulti(next);
                            if (error) setError('');
                          }}
                          className={classNames(
                            'flex items-center justify-between px-5 py-4 rounded-2xl border-2 transition-all font-medium',
                            checked
                              ? 'bg-gradient-primary border-transparent text-white shadow-md shadow-coral/20'
                              : 'bg-white border-slate-100 text-slate-600 hover:bg-slate-50'
                          )}
                        >
                          <span>{opt}</span>
                          <div className={classNames(
                            "w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors",
                            checked ? "bg-white border-white" : "border-slate-200"
                          )}>
                            {checked && <div className="w-2.5 h-2.5 bg-coral rounded-sm" />}
                          </div>
                        </motion.button>
                      );
                    })}
                  </div>
                )}

                {(q.type === 'short_text' || q.type === 'number') && (
                  <div className="relative">
                    <input
                      value={draftInput}
                      onChange={(e) => onInputChange(e.target.value)}
                      onKeyDown={onKeyDown}
                      placeholder={q.placeholder?.[lang] || (lang === 'ja' ? 'ここに入力してください' : '在此输入内容')}
                      inputMode={q.inputMode || (q.type === 'number' ? 'numeric' : 'text')}
                      className="w-full h-16 px-6 bg-slate-50 border-2 border-slate-100 rounded-2xl focus:outline-none focus:border-coral focus:bg-white focus:shadow-lg focus:shadow-coral/5 transition-all text-lg font-medium text-slate-800 placeholder:text-slate-300 text-center"
                    />
                  </div>
                )}

                {q.type === 'long_text' && (
                  <textarea
                    value={draftInput}
                    onChange={(e) => onInputChange(e.target.value)}
                    placeholder={q.placeholder?.[lang] || (lang === 'ja' ? '詳細をご記入ください...' : '请详细说明...')}
                    rows={6}
                    className="w-full px-6 py-4 bg-slate-50 border-2 border-slate-100 rounded-3xl focus:outline-none focus:border-coral focus:bg-white focus:shadow-lg focus:shadow-coral/5 transition-all text-base font-medium text-slate-800 placeholder:text-slate-300 resize-none text-center"
                  />
                )}
              </div>

              <AnimatePresence>
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-6 p-4 bg-red-50 border border-red-100 rounded-2xl flex items-center gap-3 text-red-600 text-sm font-bold"
                  >
                    <div className="w-1.5 h-1.5 rounded-full bg-red-600 animate-pulse" />
                    {error}
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="flex gap-4 sm:gap-6">
                <button
                  onClick={handleBack}
                  disabled={current === 0}
                  className={classNames(
                    'flex-1 h-14 rounded-2xl font-bold transition-all flex items-center justify-center gap-2',
                    current === 0
                      ? 'bg-slate-50 text-slate-300 cursor-not-allowed'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200 active:scale-95'
                  )}
                >
                  <ChevronLeft className="w-5 h-5" />
                  <span>{lang === 'ja' ? '戻る' : '上一步'}</span>
                </button>

                <button
                  onClick={handlePrimary}
                  disabled={submitting}
                  className={classNames(
                    'flex-[1.5] h-14 rounded-2xl font-bold flex items-center justify-center gap-2 text-white shadow-lg transition-all active:scale-[0.98]',
                    submitting
                      ? 'bg-slate-400 cursor-wait'
                      : 'bg-gradient-primary shadow-coral/30 hover:shadow-coral/40'
                  )}
                >
                  {submitting ? (
                    <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <span>
                        {isLastPredicted
                          ? (lang === 'ja' ? '問診を完了する' : '提交问诊票')
                          : (lang === 'ja' ? '次へ進む' : '下一步')}
                      </span>
                      {isLastPredicted ? <Send className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-20 bg-white rounded-3xl shadow-xl border border-slate-100 p-8"
            >
              <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <UserCircle className="w-10 h-10 text-slate-300" />
              </div>
              <p className="text-slate-500 font-medium text-lg leading-relaxed px-4">
                {lang === 'ja' ? '表示できる質問がありません' : '没有可显示的问题'}
              </p>
              <div className="mt-8">
                <button
                  onClick={() => navigate('/mypage')}
                  className="px-8 py-3.5 bg-gradient-primary text-white rounded-2xl font-bold shadow-lg shadow-coral/20 transition-all hover:-translate-y-0.5"
                >
                  {lang === 'ja' ? 'マイページへ' : '返回我的页面'}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <p className="mt-12 text-center text-xs text-slate-400 font-medium px-8 leading-relaxed">
          {lang === 'ja' 
            ? '※回答内容は医師の診察以外には使用されず、個人情報は厳重に管理されます。' 
            : '※您的回答仅由医生在诊疗时使用，个人信息将受到严格保护。'}
        </p>
      </main>
    </div>
  );
};

export default QuestionnaireForm;
